import { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

type ErrorBoundaryProps = {
  error: {
    message: string;
  };
};

const ErrorBoundaryFallback = ({ error }: ErrorBoundaryProps) => {
  return (
    <div>
      <div>Something went wrong</div>
      <div style={{ color: 'red' }}>{error.message}</div>
    </div>
  );
};

type Props = PropsWithChildren<{
  /**
   * Optional fallback to render instead of the default error UI. Use this to
   * keep a localized failure from being visually noisy (e.g. render `null` or a
   * subtle message when an isolated widget fails). When omitted, the default
   * "Something went wrong" fallback is shown.
   */
  fallback?: ReactNode;
  /**
   * Optional callback invoked when the boundary catches an error. Consumers
   * (e.g. apps wiring up a bug-tracker SDK) can pass a reporter here; this
   * package never imports or knows about any specific reporting SDK.
   *
   * `info` is React's `ErrorInfo` (so this forwards type-identically to
   * react-error-boundary's `onError`); note `info.componentStack` is
   * `string | null | undefined`, so coerce with `?? undefined` if a consumer
   * needs `string | undefined`.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}>;

export const ErrorBoundary = ({ children, fallback, onError }: Props) => {
  if (fallback !== undefined) {
    return (
      <ReactErrorBoundary fallback={<>{fallback}</>} onError={onError}>
        {children}
      </ReactErrorBoundary>
    );
  }
  return (
    <ReactErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={onError}>
      {children}
    </ReactErrorBoundary>
  );
};
